'use client'

import type React from 'react'
import Fuse from 'fuse.js'
import { useState, useMemo } from 'react'
import { Search } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { useBookingParams } from '@/hooks/useBookingParams'
import { Button } from './button'
import { City } from '@/payload-types'
import { useBookingBarStore } from '@/lib/context/bookingBarContext'
import { useTranslations } from 'next-intl'

type SearchItem = {
  id: string
  title: string
  slug: string | null
  countrySlug: string | null
  subtitle: string
  type: 'city' | 'hotel'
  route: string
  cityId?: number
  synxisId?: number
  docs?: number
  normalizedTitle?: string
}

type SearchResult = {
  cities: SearchItem[] | null
  hotels: SearchItem[] | null
}

interface SearchComboboxProps {
  data: City[]
  placeholder?: string
  handleBookingTriggers?: (type: 'dates') => void
  open: boolean
  setOpen: (open: boolean) => void
  route?: boolean
  hotelId?: string | null
}

export function SearchCombobox({
  data,
  placeholder = '',
  handleBookingTriggers,
  open,
  setOpen,
  route = false,
  hotelId,
}: SearchComboboxProps) {
  const [query, setQuery] = useState('')
  const [selectedItem, setSelectedItem] = useState<SearchItem | null>(null)

  const t = useTranslations()

  const { updateParams } = useBookingParams()

  const { citySlug, countrySlug, setCountrySlug, setCitySlug, filter } = useBookingBarStore()

  const normalizeString = (str: string | undefined) => {
    if (!str) return ''

    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  }

  const transformDataToSearchResults = (payloadData: City[]) => {
    const searchResults: SearchItem[] = []

    payloadData.forEach((city) => {
      const country = city.country
      if (typeof country === 'object') {
        // Add city as a search result
        searchResults.push({
          id: `city-${city.id}`,
          slug: city.slug || null,
          countrySlug: country.slug || null,
          title: city.title,
          type: 'city',
          subtitle: country.title,
          cityId: city.id,
          synxisId:
            city.hotels?.docs?.length === 1 && typeof city.hotels?.docs[0] === 'object'
              ? city.hotels?.docs[0].synxis_id || undefined
              : undefined,
          docs: city.hotels?.docs?.length,
          route: `/hotels/${country.slug}/${city.slug}`,
          normalizedTitle: normalizeString(city.title),
        })

        // Add hotels as search results
        city.hotels?.docs?.forEach((hotel) => {
          if (typeof hotel === 'object') {
            searchResults.push({
              title: hotel.title,
              id: `hotel-${hotel.id}`,
              slug: hotel.slug || null,
              countrySlug: null,
              type: 'hotel',
              subtitle: `${city.title}, ${country.title}`,
              cityId: city.id,
              synxisId: hotel.synxis_id || undefined,
              route: `/hotels/${country.slug}/${city.slug}/${hotel.slug}`,
              normalizedTitle: normalizeString(hotel.title),
            })
          }
        })
      }
    })

    return searchResults
  }

  const allResults = transformDataToSearchResults(data)
  const cityResults = allResults.filter((r) => r.type === 'city')
  const hotelResults = allResults.filter((r) => r.type === 'hotel')

  const fuse = new Fuse(allResults, {
    keys: [
      { name: 'normalizedTitle', weight: 3 }, // Normalized title for better special character matching
      { name: 'title', weight: 2 }, // Title matches are more important
      { name: 'subtitle', weight: 1 }, // Subtitle matches are secondary
    ],
    threshold: 0.3, // Stricter matching for better precision
    distance: 100, // Reduced distance for more relevant results
    includeScore: true,
    ignoreLocation: false, // Enable location awareness
    findAllMatches: true,
    // minMatchCharLength: 2, // Require at least 2 characters to match
    useExtendedSearch: false,
  })

  const results = useMemo<SearchResult>(() => {
    if (!query.trim() && filter) {
      return { hotels: hotelResults, cities: null }
    } else if (!query.trim()) {
      return { hotels: null, cities: cityResults }
    }

    const searchResults = fuse.search(query)
    return {
      cities: searchResults.filter((r) => r.item.type === 'city').map((r) => r.item),
      hotels: searchResults.filter((r) => r.item.type === 'hotel').map((r) => r.item),
    }
  }, [query, data, filter])

  // Show what the search will actually use: the hotel param, else the city in the store.
  // A local selection only wins while it still matches that state.
  const displayItem = useMemo(() => {
    if (Number(hotelId)) {
      if (selectedItem?.synxisId === Number(hotelId)) return selectedItem

      const hit = allResults.filter((r) => r.synxisId === Number(hotelId))
      return hit.find((h) => h.type === 'hotel') ?? hit[0] ?? null
    }

    if (citySlug) {
      if (selectedItem?.type === 'city' && selectedItem.slug === citySlug) return selectedItem

      return (
        cityResults.find((c) => c.slug === citySlug && c.countrySlug === countrySlug) ?? null
      )
    }

    return null
  }, [hotelId, citySlug, countrySlug, selectedItem, data])

  const handleSelect = (item: SearchItem) => {
    setSelectedItem(item)
    setOpen(false)

    if (item.type === 'city' && item.docs && item.docs > 1) {
      setCitySlug(item.slug || null)
      setCountrySlug(item.countrySlug || null)
      updateParams((params) => params.delete('hotel'))
    } else if (item.type === 'city' && item.docs && item.docs === 1) {
      setCitySlug(null)
      setCountrySlug(null)
      updateParams((params) => params.set('hotel', item.synxisId?.toString() || ''))
    } else {
      setCitySlug(null)
      setCountrySlug(null)
      updateParams((params) => params.set('hotel', item.synxisId?.toString() || ''))
    }

    if (handleBookingTriggers) handleBookingTriggers('dates')
  }

  return (
    <div className='relative w-full h-full'>
      <div className='relative w-full h-full'>
        <Search
          className={cn('absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted-foreground', {
            'text-foreground': displayItem !== null,
          })}
          strokeWidth={1.5}
        />

        <button
          onClick={() => setOpen(true)}
          className={cn(
            'pl-10 w-full rounded-lg md:shadow-md justify-start text-base bg-input border-secondary text-muted-foreground truncate h-full font-normal hover:bg-background border transition-colors py-3',
            {
              'text-foreground': displayItem !== null,
            },
          )}
        >
          <p className='max-w-10/12 truncate text-left'>
            {displayItem ? displayItem.title : placeholder}
          </p>
        </button>
      </div>

      <CommandDialog open={open} onOpenChange={setOpen} showCloseButton={false}>
        <CommandInput placeholder={placeholder} value={query} onValueChange={setQuery} autoFocus />
        <CommandList>
          <CommandEmpty>{t('no_results_found')}</CommandEmpty>
          {results?.cities && results?.cities.length > 0 && (
            <CommandGroup heading='Cities'>
              {results?.cities.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.title}
                  onSelect={() => handleSelect(item)}
                  className='flex justify-between'
                >
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-2'>
                      <span className='font-medium'>{item.title}</span>
                    </div>
                    <span className='text-sm text-muted-foreground'>{item.subtitle}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <span className='text-muted-foreground'>({item.docs || 0})</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          <CommandSeparator />

          {results?.hotels && results?.hotels.length > 0 && (
            <CommandGroup heading='Hotels'>
              {results?.hotels?.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.title}
                  onSelect={() => handleSelect(item)}
                  className='flex justify-between'
                >
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-2'>
                      <span className='font-medium'>{item.title}</span>
                    </div>
                    <span className='text-sm text-muted-foreground'>{item.subtitle}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </div>
  )
}
